// Single source of truth for SceneKind ↔ Blender master mapping.
//
// `kind` is a stable kebab-case string used throughout the web app.
// `master` is the name of the corresponding Blender object (ends in "_Master").
//
// HUMAN-CURATED. The bake script (tools/export_blender.py) reads this file at
// bake time to populate manifest.kindMap. Keep one entry per line, predictable
// structure — the script extracts entries with a simple regex.
//
// Renaming a Blender master? Update only the `master:` field here.
// Adding a new asset? Add a row here AND build a new *_Master in the .blend.
// Removing an asset? Remove the row AND the master in the .blend.

export type SceneKind =
  | "dedicated-rack"
  | "modular-pod"
  | "server-rack"
  | "ai-cluster"
  | "gpu-stack"
  | "inference-node"
  | "storage-array"
  | "control-hub"
  | "wind-turbine"
  | "solar-array"
  | "office-block"
  | "warehouse"
  | "parking-lot"
  | "tree-cluster"
  | "road"
  | "road-wide"
  | "canal"
  // Network floor (Iso-Network pass)
  | "subsea-cable"
  | "landing-station"
  | "pop-node"
  | "route-router"
  | "interconnection-hub"
  | "low-latency-switch"
  | "security-gateway"
  | "fibre-line"
  | "satellite-link"
  | "network-control-terminal"
  // Brand marks placeable as first-class objects (in addition to being
  // available as decals via the Brand-mark inspector controls).
  | "icon-mark"
  | "wordmark"
  // Runtime-generated Brand object. It has no Blender master.
  | "text-label"
  // AI/GPU data-hall kit (Rack_* + tray + tile masters)
  | "rack-ai"
  | "rack-endcap"
  | "cable-tray"
  | "floor-tile"
  // Runtime-composite. No Blender master — stamps N copies of "rack-ai"
  // (plus optional end caps & overhead trays) at runtime via lib/rackRow.ts.
  | "rack-row";

export type SceneKindGroup = "AI" | "Infra" | "Power" | "Site" | "Network" | "Brand";

export type SceneKindEntry = {
  kind: SceneKind;
  master: string;
  label: string;
  group: SceneKindGroup;
};

// Order in this array defines the catalog UI ordering.
export const SCENE_KINDS: SceneKindEntry[] = [
  { kind: "dedicated-rack",  master: "DSR_Master",  label: "Dedicated rack",  group: "AI"    },
  { kind: "modular-pod",     master: "MP_Master",   label: "Modular pod",     group: "AI"    },
  { kind: "ai-cluster",      master: "AC_Master",   label: "AI cluster",      group: "AI"    },
  { kind: "inference-node",  master: "IN_Master",   label: "Inference node",  group: "AI"    },
  { kind: "control-hub",     master: "CH_Master",   label: "Control hub",     group: "AI"    },
  { kind: "server-rack",     master: "DR_Master",   label: "Server rack",     group: "Infra" },
  { kind: "gpu-stack",       master: "GP_Master",   label: "GPU stack",       group: "Infra" },
  { kind: "storage-array",   master: "SA2_Master",  label: "Storage array",   group: "Infra" },
  { kind: "wind-turbine",    master: "WT_Master",   label: "Wind turbine",    group: "Power" },
  { kind: "solar-array",     master: "SP_Master",   label: "Solar array",     group: "Power" },
  { kind: "office-block",    master: "OB_Master",   label: "Office block",    group: "Site"  },
  { kind: "warehouse",       master: "WH_Master",   label: "Warehouse",       group: "Site"  },
  { kind: "parking-lot",     master: "PL_Master",   label: "Parking lot",     group: "Site"  },
  { kind: "tree-cluster",    master: "TR_Master",   label: "Trees",           group: "Site"  },
  { kind: "road",            master: "RD_Master",   label: "Road",            group: "Site"  },
  { kind: "road-wide",       master: "RW_Master",   label: "Road (wide)",     group: "Site"  },
  { kind: "canal",           master: "CN_Master",   label: "Canal",           group: "Site"  },
  { kind: "subsea-cable",       master: "SUB_Master",  label: "Subsea cable",        group: "Network" },
  { kind: "landing-station",    master: "LST_Master",  label: "Landing station",     group: "Network" },
  { kind: "pop-node",           master: "POP_Master",  label: "PoP node",            group: "Network" },
  { kind: "route-router",       master: "RTR_Master",  label: "Router",              group: "Network" },
  { kind: "interconnection-hub",master: "IXP_Master",  label: "Interconnection hub", group: "Network" },
  { kind: "low-latency-switch", master: "LSW_Master",  label: "Low-latency switch",  group: "Network" },
  { kind: "security-gateway",   master: "SEC_Master",  label: "Security gateway",    group: "Network" },
  { kind: "fibre-line",         master: "FBR_Master",  label: "Fibre line",          group: "Network" },
  { kind: "satellite-link",     master: "SAT_Master",  label: "Satellite link",      group: "Network" },
  { kind: "network-control-terminal", master: "NCT_Master", label: "Network control",group: "Network" },
  { kind: "icon-mark",          master: "IconMark_Master", label: "Icon mark",     group: "Brand" },
  { kind: "wordmark",           master: "Logo_Master",     label: "Wordmark",      group: "Brand" },
  // AI/GPU data-hall kit
  { kind: "rack-ai",            master: "Rack_AI_Master",     label: "AI rack",        group: "AI"    },
  { kind: "rack-endcap",        master: "Rack_EndCap_Master", label: "Rack end cap",   group: "AI"    },
  { kind: "cable-tray",         master: "CableTray_Master",   label: "Cable tray",     group: "Infra" },
  { kind: "floor-tile",         master: "FloorTile_Master",   label: "Floor tile",     group: "Site"  },
  // NOTE: "rack-row" is a runtime composite (no Blender master). Catalog
  // entry is injected by lib/rackRow.ts → addVirtualRackRowEntry().
];

// Lookup helpers used by the registry and UI.
export const KIND_TO_MASTER: Record<SceneKind, string> = Object.fromEntries(
  SCENE_KINDS.map((e) => [e.kind, e.master])
) as Record<SceneKind, string>;

export const MASTER_TO_KIND: Record<string, SceneKind> = Object.fromEntries(
  SCENE_KINDS.map((e) => [e.master, e.kind])
);

export function getKindLabel(kind: SceneKind): string {
  return SCENE_KINDS.find((e) => e.kind === kind)?.label ?? kind;
}
